import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Switch, Select, Text, Button, RadioGroup, Icon, Loader} from '@gravity-ui/uikit';
import {TrashBin, Plus} from '@gravity-ui/icons';
import {
  ActionModal,
  FormInput,
  useErrorHandler,
  ErrorModal,
  FileUploadDropzone,
  FilePreviewInformation,
} from '../../../../shared';
import {CommunicationMediaType, CommunicationVideoStyle} from '../../../../entities';
import {useAddCommunicationMessageForm} from '../../model';
import {useDocumentUpload} from '../../../fundraiserDocuments';
import {ImageUploadButton, PreparedImageData, fundraiserImageApi} from '../../../fundraiserImages';
import styles from './AddCommunicationMessageModal.module.scss';

interface AddCommunicationMessageModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const GROUP_OPTIONS = [
  {value: 'ProjectNews', content: 'Новости проектов'},
  {value: 'AppealsAndReports', content: 'Сборы и отчеты'},
  {value: 'Ambassadors', content: 'Амбассадоры'},
  {value: 'TechnicalSupport', content: 'Техподдержка'},
];

const RECIPIENT_OPTIONS = [
  {value: 'conditions', content: 'По условиям'},
  {value: 'file', content: 'По списку из файла'},
];

const CONDITIONS_OPTIONS = [
  {value: 'All', content: 'Все'},
  {value: 'NeverDonated', content: 'Не донатил ни разу'},
  {value: 'DonatedOnce', content: 'Донатили один раз'},
  {value: 'DonatedTwice', content: 'Донатили два раза'},
  {value: 'DonatedThreeOrMoreTimes', content: 'Донатили три и более раз'},
  {value: 'WithActiveSubscription', content: 'Есть действующая подписка'},
  {value: 'WithoutActiveSubscription', content: 'Нет действующей подписки'},
  {value: 'WithoutActiveButWithInactiveSubscription', content: 'Есть неактивная подписка'},
];

const MEDIA_TYPE_OPTIONS = [
  {value: 'image', content: 'Изображение'},
  {value: 'video', content: 'Видео'},
];

const VIDEO_STYLE_OPTIONS = [
  {value: 'Circle', content: 'Кружок'},
  {value: 'Rectangle', content: 'Вертикальное'},
];

export const AddCommunicationMessageModal = ({open, setOpen}: AddCommunicationMessageModalProps) => {
  const [recipientType, setRecipientType] = useState<'conditions' | 'file'>('conditions');
  const [selectedCondition, setSelectedCondition] = useState<string>('All');
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    isLoading,
    submitError,
    resetForm,
    submitForm,
  } = useAddCommunicationMessageForm({setOpen});

  const {uploadId, fileName, fileSize, isUploading, uploadDocument, resetUpload} = useDocumentUpload();

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Уведомление',
    title: 'Ошибка при создании уведомления',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  useEffect(() => {
    if (open) {
      resetForm();
      resetUpload();
      setRecipientType('conditions');
      setSelectedCondition('All');
      setIsVideoUploading(false);
    }
    // eslint-disable-next-line
  }, [open]);

  // Обновляем uploadId в форме когда файл загружен
  useEffect(() => {
    if (uploadId) {
      setFieldValue('recipientsFileUploadId', uploadId);
    } else {
      setFieldValue('recipientsFileUploadId', null);
    }
  }, [uploadId, setFieldValue]);

  const handleSwitchChange = (checked: boolean) => {
    setFieldValue('showButton', checked);
    if (!checked) {
      setFieldValue('button', null);
    } else {
      setFieldValue('button', {text: '', link: ''});
    }
  };

  const handleButtonTextChange = (value: string) => {
    const currentButton = values.button || {text: '', link: ''};
    setFieldValue('button', {...currentButton, text: value});
  };

  const handleButtonLinkChange = (value: string) => {
    const currentButton = values.button || {text: '', link: ''};
    setFieldValue('button', {...currentButton, link: value});
  };

  const handleImagePrepared = (data: PreparedImageData, previewUrlFromUpload: string) => {
    setFieldValue('imageUploadId', data.uploadId);
    setFieldValue('imagePreview', previewUrlFromUpload);
  };

  const handleRemoveImage = () => {
    setFieldValue('imageUploadId', null);
    setFieldValue('imagePreview', null);
  };

  const handleVideoSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) return;
      const file = event.target.files[0];

      if (!file.type.startsWith('video/')) return;

      setIsVideoUploading(true);
      try {
        const response = await fundraiserImageApi.prepareUpload([file.type]);
        if (!response.items || response.items.length === 0 || !response.items[0].url) {
          throw new Error('Ошибка подготовки загрузки видео');
        }

        const uploadItem = response.items[0];
        const buffer = await file.arrayBuffer();
        const uploadResponse = await fetch(uploadItem.url, {
          method: 'PUT',
          headers: {'Content-Type': file.type},
          body: buffer,
        });

        if (!uploadResponse.ok) {
          throw new Error('Ошибка загрузки видео');
        }

        const previewUrl = URL.createObjectURL(file);
        setFieldValue('videoUploadId', uploadItem.id);
        setFieldValue('videoPreview', previewUrl);
      } catch (error) {
        console.error('Ошибка загрузки видео:', error);
      } finally {
        setIsVideoUploading(false);
        event.target.value = '';
      }
    },
    [setFieldValue],
  );

  const handleRemoveVideo = () => {
    setFieldValue('videoUploadId', null);
    setFieldValue('videoPreview', null);
  };

  const handleMediaTypeChange = (value: string[]) => {
    const newType = value[0] as CommunicationMediaType;
    setFieldValue('mediaType', newType);
    // Сбрасываем данные другого типа медиа при смене
    if (newType === 'image') {
      setFieldValue('videoUploadId', null);
      setFieldValue('videoPreview', null);
    } else {
      setFieldValue('imageUploadId', null);
      setFieldValue('imagePreview', null);
    }
  };

  const handleVideoStyleChange = (value: string) => {
    setFieldValue('videoStyle', value as CommunicationVideoStyle);
  };

  const handleFileSelect = (file: File) => {
    uploadDocument(file);
  };

  const handleRecipientTypeChange = (value: string) => {
    const newType = value as 'conditions' | 'file';
    setRecipientType(newType);

    if (newType === 'conditions') {
      setFieldValue('recipientsSelector', 'All');
      setFieldValue('recipientsFileUploadId', null);
      resetUpload();
    } else {
      setFieldValue('recipientsSelector', 'FromFile');
    }
  };

  const handleConditionChange = (value: string[]) => {
    const condition = value[0];
    setSelectedCondition(condition);
    setFieldValue('recipientsSelector', condition);
  };

  const handleRemoveFile = () => {
    resetUpload();
    setFieldValue('recipientsFileUploadId', null);
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size="m"
        buttonsSize="l"
        caption="Создать сообщение"
        textButtonApply="Создать"
        textButtonCancel="Отменить">
        <div className={styles.form}>
          <div className={styles.selectField}>
            <label className={styles.selectLabel}>Группа</label>
            <Select
              size="xl"
              width="max"
              placeholder="Выберите группу"
              value={[values.group]}
              onUpdate={(value) => setFieldValue('group', value[0])}
              options={GROUP_OPTIONS}
            />
            {touched.group && errors.group && (
              <Text variant="caption-2" color="danger">
                {errors.group}
              </Text>
            )}
          </div>
          <div className={styles.selectField}>
            <label className={styles.selectLabel}>Получатели</label>
            <RadioGroup
              size="l"
              value={recipientType}
              onUpdate={handleRecipientTypeChange}
              options={RECIPIENT_OPTIONS}
            />
            {touched.recipientsSelector && errors.recipientsSelector && (
              <Text variant="caption-2" color="danger">
                {errors.recipientsSelector}
              </Text>
            )}
          </div>

          {recipientType === 'conditions' && (
            <div className={styles.selectField}>
              <label className={styles.selectLabel}>
                Список условий <span>*</span>
              </label>
              <Select
                size="xl"
                width="max"
                placeholder="Выберите условие"
                value={[selectedCondition]}
                onUpdate={handleConditionChange}
                options={CONDITIONS_OPTIONS}
              />
            </div>
          )}

          {recipientType === 'file' && (
            <div className={styles.fileField}>
              <label className={styles.fileLabel}>
                Список ID <span>*</span>
              </label>

              {!uploadId && !values.recipientsFileUploadId ? (
                <div className={styles.fileUploadContainer}>
                  <FileUploadDropzone
                    onFileSelect={handleFileSelect}
                    accept=".txt,.csv"
                    maxSize={25 * 1024 * 1024}
                    isLoading={isUploading}
                  />
                  <Text variant="body-2">
                    Необходимо загружать текстовый файл, содержащий по одному идентификатору пользователя на строку
                  </Text>
                </div>
              ) : (
                <FilePreviewInformation fileName={fileName} fileSize={fileSize} handleRemoveFile={handleRemoveFile} />
              )}

              {touched.recipientsFileUploadId && errors.recipientsFileUploadId && (
                <Text variant="caption-2" color="danger">
                  {errors.recipientsFileUploadId}
                </Text>
              )}
            </div>
          )}

          <FormInput
            label="Заголовок"
            placeholder="Заголовок уведомления"
            name="subject"
            value={values.subject}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            error={touched.subject && errors.subject}
            isTextarea
          />

          <FormInput
            label="Текст"
            placeholder="Текст уведомления"
            name="body"
            value={values.body}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            error={touched.body && errors.body}
            isTextarea
          />

          <div className={styles.switchField}>
            <label className={styles.switchLabel}>Показывать кнопку</label>
            <Switch size="l" checked={values.showButton} onUpdate={handleSwitchChange} />
          </div>

          {values.showButton && (
            <div className={styles.buttonFields}>
              <FormInput
                name="buttonText"
                label="Текст кнопки"
                placeholder="Введите текст кнопки"
                value={(values.button as any)?.text || ''}
                onChange={(e) => handleButtonTextChange(e.target.value)}
                error={touched.button && typeof errors.button === 'object' && (errors.button as any)?.text}
              />
              <FormInput
                name="buttonLink"
                label="Ссылка кнопки"
                placeholder="https://example.com"
                value={(values.button as any)?.link || ''}
                onChange={(e) => handleButtonLinkChange(e.target.value)}
                error={touched.button && typeof errors.button === 'object' && (errors.button as any)?.link}
              />
            </div>
          )}

          <div className={styles.selectField}>
            <label className={styles.selectLabel}>Медиа</label>
            <Select
              size="xl"
              width="max"
              value={[values.mediaType]}
              onUpdate={handleMediaTypeChange}
              options={MEDIA_TYPE_OPTIONS}
            />
          </div>

          {values.mediaType === 'image' && (
            <div className={styles.imageField}>
              <label className={styles.imageLabel}>Изображение</label>

              {!values.imagePreview && (
                <div className={styles.imageUploadContainer}>
                  <ImageUploadButton
                    isCreating={true}
                    aspectRatio={16 / 9}
                    onPrepareImageData={handleImagePrepared}
                    className={styles.imageUploadButton}
                    withBg
                  />
                </div>
              )}

              {values.imagePreview && (
                <div className={styles.previewImageContainer}>
                  <img src={values.imagePreview} alt="Превью" className={styles.imagePreview} />
                  <Button view="raised" size="xs" onClick={handleRemoveImage} className={styles.removeImageBtn}>
                    <Icon data={TrashBin} size={14} />
                  </Button>
                </div>
              )}
            </div>
          )}

          {values.mediaType === 'video' && (
            <>
              <div className={styles.selectField}>
                <label className={styles.selectLabel}>Видео</label>
                <RadioGroup
                  size="l"
                  value={values.videoStyle}
                  onUpdate={handleVideoStyleChange}
                  options={VIDEO_STYLE_OPTIONS}
                />
              </div>

              <div className={styles.imageField}>
                <label className={styles.imageLabel}>Файл видео</label>
                <div className={styles.videoUploadArea}>
                  {!values.videoPreview && !isVideoUploading && (
                    <>
                      <div
                        className={
                          values.videoStyle === 'Circle' ? styles.videoUploadCircle : styles.videoUploadRectangle
                        }>
                        <Button view="normal" size="xl" onClick={() => videoInputRef.current?.click()}>
                          <Icon data={Plus} size={20} />
                        </Button>
                      </div>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/mp4,video/webm"
                        style={{display: 'none'}}
                        onChange={handleVideoSelect}
                      />
                    </>
                  )}
                  {isVideoUploading && (
                    <div className={styles.videoLoading}>
                      <Loader size="s" />
                      <span>Загрузка видео...</span>
                    </div>
                  )}
                  {values.videoPreview && !isVideoUploading && (
                    <div className={styles.videoPreviewContainer}>
                      <div
                        className={
                          values.videoStyle === 'Circle' ? styles.videoPreviewCircle : styles.videoPreviewRectangle
                        }>
                        <video src={values.videoPreview} className={styles.videoElement} muted />
                      </div>
                      <Button view="raised" size="xs" onClick={handleRemoveVideo} className={styles.removeVideoBtn}>
                        <Icon data={TrashBin} size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
